import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// 支持的语言列表
export const supportedLanguages = ['en', 'zh', 'zh-TW', 'ja', 'ko', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ar', 'hi', 'th', 'vi', 'tr', 'nl', 'pl', 'sv'];

// 获取浏览器语言 - 默认返回英文
export const getBrowserLanguage = (): string => {
  // 始终默认返回英文，除非用户主动选择其他语言
  return 'en';
};

// 从URL中提取语言
export const getLanguageFromPath = (pathname: string): string | null => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (supportedLanguages.includes(firstSegment)) {
    return firstSegment;
  }
  
  return null;
};

// 移除URL中的语言前缀
export const removeLanguageFromPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (supportedLanguages.includes(firstSegment)) {
    return '/' + segments.slice(1).join('/');
  }
  
  return pathname;
};

// 添加语言前缀到URL
export const addLanguageToPath = (pathname: string, language: string): string => {
  const cleanPath = removeLanguageFromPath(pathname);
  
  if (language === 'en') {
    return cleanPath || '/';
  }
  
  return `/${language}${cleanPath}`;
};

// 获取本地化的路径
export const getLocalizedPath = (path: string, language: string): string => {
  if (language === 'en') {
    return path;
  }
  
  return `/${language}${path}`;
};

// Hook for handling language routing
export const useLanguageRouter = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const urlLanguage = getLanguageFromPath(pathname);

    // 只在URL中有明确的语言代码时才切换语言
    if (urlLanguage && supportedLanguages.includes(urlLanguage)) {
      if (i18n.language !== urlLanguage) {
        i18n.changeLanguage(urlLanguage);
      }
    } else {
      // 如果URL中没有语言代码，确保使用英文（但不重定向）
      if (i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }
    }
  }, [location.pathname, i18n]);

  // 语言切换函数
  const changeLanguage = (newLanguage: string) => {
    const cleanPath = removeLanguageFromPath(location.pathname);

    // 如果切换到英文，直接使用无前缀的路径
    const newPath = newLanguage === 'en' ? cleanPath : addLanguageToPath(cleanPath, newLanguage);

    // 标记用户主动选择了语言
    localStorage.setItem('userSelectedLanguage', 'true');
    localStorage.setItem('selectedLanguage', newLanguage);

    i18n.changeLanguage(newLanguage);
    navigate(newPath);
  };

  return {
    changeLanguage,
    currentLanguage: i18n.language,
    getLocalizedPath: (path: string) => getLocalizedPath(path, i18n.language)
  };
};

// 生成hreflang链接
export const generateHrefLangLinks = (currentPath: string) => {
  const cleanPath = removeLanguageFromPath(currentPath);
  
  return supportedLanguages.map(lang => ({
    hrefLang: lang,
    href: addLanguageToPath(cleanPath, lang)
  }));
};

// 语言检测和重定向中间件 - 不再自动重定向
export const languageMiddleware = (_pathname: string): string | null => {
  // 不再自动重定向，让用户主动选择语言
  return null;
};
